import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import CreateOrderModal from './CreateOrderModal';
import EditOrderModal from './EditOrderModal';
import BanquetsTab from './BanquetsTab';
import { ymdInStaffTz, addDaysYmd } from '../../utils/staffReportDates';
import { formatRestaurantPrice, normalizeCurrency } from '../../utils/formatPrice';

const STATUS_LABELS = {
  pending:   { label: 'Новый',    color: '#FF9500', bg: '#FFF3E0' },
  confirmed: { label: 'Принят',   color: '#007AFF', bg: '#E3F2FD' },
  ready:     { label: 'Готов',    color: '#34C759', bg: '#E8F5E9' },
  completed: { label: 'Выдан',    color: '#8E8E93', bg: '#F5F5F5' },
  cancelled: { label: 'Отменён',  color: '#E85D4A', bg: '#FFEBEE' },
};

const CALL_LABELS = {
  waiter: { icon: '🔔', label: 'Вызов официанта', color: '#FF9500', bg: '#FFF3E0' },
  bill:   { icon: '💳', label: 'Просят счёт',     color: '#7B1FA2', bg: '#F3E5F5' },
};

const fmtT = (d) => new Date(d).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
const fmtD = (d) => new Date(d).toLocaleDateString('ru-RU', {
  day: 'numeric', month: 'long', year: 'numeric', weekday: 'long',
});

function groupByDay(orders) {
  const groups = {};
  orders.forEach((o) => {
    const day = fmtD(o.created_at);
    if (!groups[day]) groups[day] = [];
    groups[day].push(o);
  });
  return groups;
}

function sumMoneyByCurrency(orderList) {
  const m = { UZS: 0, RUB: 0 };
  for (const o of orderList) {
    if (o.status === 'cancelled') continue;
    const c = normalizeCurrency(o.currency);
    m[c] = (m[c] || 0) + Number(o.total_amount);
  }
  return m;
}

function formatMoneyMix(orderList) {
  const m = sumMoneyByCurrency(orderList);
  const parts = [];
  if (m.UZS) parts.push(formatRestaurantPrice(m.UZS, 'UZS'));
  if (m.RUB) parts.push(formatRestaurantPrice(m.RUB, 'RUB'));
  return parts.join(' · ') || formatRestaurantPrice(0, 'UZS');
}

export default function OrdersTab({
  api,
  restaurantId,
  userRole,
  menuItems = [],
  categories = [],
  menuCurrency = 'UZS',
}) {
  const [tab,           setTab]           = useState('orders');
  const [showCreateOrder, setShowCreateOrder] = useState(false);
  const [createOrderLoading, setCreateOrderLoading] = useState(false);
  const [editOrderId, setEditOrderId] = useState(null);
  const [editOrderLoading, setEditOrderLoading] = useState(false);
  const [orders,        setOrders]        = useState([]);
  const [calls,         setCalls]         = useState([]);
  const [stats,         setStats]         = useState(null);
  const [message,       setMessage]       = useState('');
  const [period,        setPeriod]        = useState('today');
  const [dateFrom,      setDateFrom]      = useState(() => ymdInStaffTz());
  const [dateTo,        setDateTo]        = useState(() => ymdInStaffTz());
  const [statusFilter,  setStatusFilter]  = useState('all');
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [orderHistory,   setOrderHistory]   = useState({});

  const ordersMixedCurrency = useMemo(() => {
    const s = new Set(orders.map((o) => normalizeCurrency(o.currency)));
    return s.size > 1;
  }, [orders]);

  const prevOrderIds = useRef(new Set());
  const callsPrimedRef = useRef(false);
  const notifiedCallIdsRef = useRef(new Set());
  const audioCtxRef  = useRef(null);

  const showMsg = (m) => { setMessage(m); setTimeout(() => setMessage(''), 3000); };

  // Разблокируем AudioContext при первом взаимодействии пользователя
  useEffect(() => {
    const unlock = () => {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (audioCtxRef.current.state === 'suspended') {
        audioCtxRef.current.resume();
      }
    };
    document.addEventListener('click', unlock, { once: true });
    document.addEventListener('touchstart', unlock, { once: true });
    return () => {
      document.removeEventListener('click', unlock);
      document.removeEventListener('touchstart', unlock);
    };
  }, []);

  const playBeep = useCallback((type = 'order') => {
    try {
      const ctx = audioCtxRef.current;
      if (!ctx || ctx.state === 'suspended') return;
      const notes = type === 'order' ? [880, 1100] : [660, 880, 1100];
      notes.forEach((freq, i) => {
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.value = freq;
        const t = ctx.currentTime + i * 0.18;
        gain.gain.setValueAtTime(0.4, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
        osc.start(t);
        osc.stop(t + 0.25);
      });
    } catch {}
  }, []);

  const vibrate = useCallback((pattern = [150, 80, 150]) => {
    try { if (navigator.vibrate) navigator.vibrate(pattern); } catch {}
  }, []);

  const getRange = useCallback(() => {
    const today = ymdInStaffTz(new Date());
    if (period === 'today')     return { from: today, to: today };
    if (period === 'yesterday') {
      const y = addDaysYmd(today, -1);
      return { from: y, to: y };
    }
    if (period === 'week')      return { from: addDaysYmd(today, -6), to: today };
    if (period === 'month')     return { from: addDaysYmd(today, -29), to: today };
    if (period === 'all')       return { from: '2024-01-01', to: '2099-12-31' };
    return { from: dateFrom, to: dateTo };
  }, [period, dateFrom, dateTo]);

  const fetchOrders = useCallback(async () => {
    if (!restaurantId) return;
    const { from, to } = getRange();
    try {
      const { data } = await api.get(
        `/api/staff/orders?restaurantId=${restaurantId}&from=${from}&to=${to}`
      );
      const incoming = data.orders || [];
      const newOnes = incoming.filter((o) => !prevOrderIds.current.has(o.id) && !o.is_read);
      if (newOnes.length > 0 && prevOrderIds.current.size > 0) {
        playBeep('order');
        vibrate([150, 80, 150]);
        newOnes.forEach((o) =>
          showMsg(`🆕 Новый заказ #${o.order_number} — Стол ${o.table_number || '?'}`)
        );
        /* Системные push в фоне — с сервера (Web Push + VAPID), не дублировать new Notification() */
      }
      prevOrderIds.current = new Set(incoming.map((o) => o.id));
      setOrders(incoming);
    } catch (err) { console.error('fetchOrders:', err); }
  }, [api, restaurantId, getRange, playBeep, vibrate]);

  const fetchCalls = useCallback(async () => {
    if (!restaurantId) return;
    const { from, to } = getRange();
    try {
      const { data } = await api.get(
        `/api/staff/calls?restaurantId=${restaurantId}&from=${from}&to=${to}`
      );
      const incoming = data.calls || [];

      // Первый ответ — без звука (бэклог), запоминаем все id
      if (!callsPrimedRef.current) {
        callsPrimedRef.current = true;
        incoming.forEach((c) => notifiedCallIdsRef.current.add(c.id));
        setCalls(incoming);
        return;
      }

      const now = Date.now();
      const newOnes = incoming.filter((c) => {
        if (c.is_read || notifiedCallIdsRef.current.has(c.id)) return false;
        const created = new Date(c.created_at).getTime();
        return Number.isFinite(created) && now - created < 3 * 60 * 1000;
      });

      if (newOnes.length > 0) {
        playBeep('call');
        vibrate([200, 100, 200, 100, 200]);
        newOnes.forEach((c) => {
          notifiedCallIdsRef.current.add(c.id);
          const lbl = CALL_LABELS[c.type] || CALL_LABELS.waiter;
          showMsg(`${lbl.icon} ${lbl.label} — Стол ${c.table_number || '?'}`);
        });
      }

      incoming.forEach((c) => notifiedCallIdsRef.current.add(c.id));
      setCalls(incoming);
    } catch (err) { console.error('fetchCalls:', err); }
  }, [api, restaurantId, getRange, playBeep, vibrate]);

  const fetchStats = useCallback(async () => {
    if (!restaurantId || userRole === 'waiter') return;
    const { from, to } = getRange();
    try {
      const { data } = await api.get(
        `/api/staff/stats?restaurantId=${restaurantId}&from=${from}&to=${to}`
      );
      setStats(data);
    } catch (err) { console.error('fetchStats:', err); }
  }, [api, restaurantId, getRange, userRole]);

  useEffect(() => {
    fetchOrders();
    fetchCalls();
    fetchStats();
  }, [period, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Автообновление каждые 30 сек
  useEffect(() => {
    const iv = setInterval(() => { fetchOrders(); fetchCalls(); }, 10000);
    return () => clearInterval(iv);
  }, [fetchOrders, fetchCalls]);

  useEffect(() => {
    if (tab === 'stats') fetchStats();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Убрать «залипший» тост вызова при входе на вкладку «Вызовы»
  useEffect(() => {
    if (tab === 'calls') setMessage('');
  }, [tab]);

  // Бейдж на вкладке браузера
  useEffect(() => {
    const unread = orders.filter((o) => !o.is_read).length + calls.filter((c) => !c.is_read).length;
    document.title = unread > 0 ? `(${unread}) Заказы` : 'Меню';
    return () => { document.title = 'Меню'; };
  }, [orders, calls]);

  // При переходе на вкладку Заказы — помечаем все как прочитанные
  useEffect(() => {
    if (tab !== 'orders' || !restaurantId) return;
    const unread = orders.filter((o) => !o.is_read);
    if (unread.length === 0) return;
    Promise.all(
      unread.map((o) =>
        api.patch(`/api/staff/orders/${o.id}/read?restaurantId=${restaurantId}`)
      )
    ).then(() => {
      setOrders((prev) => prev.map((o) => ({ ...o, is_read: true })));
    }).catch(() => {});
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOrderHistory = useCallback(async (orderId) => {
    if (!restaurantId) return;
    try {
      const { data } = await api.get(
        `/api/staff/orders/${orderId}/history?restaurantId=${restaurantId}`
      );
      setOrderHistory((prev) => ({ ...prev, [orderId]: data.history || [] }));
    } catch { setOrderHistory((prev) => ({ ...prev, [orderId]: [] })); }
  }, [api, restaurantId]);

  const toggleOrderExpand = (orderId) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
    if (expandedOrderId !== orderId && !orderHistory[orderId]) {
      fetchOrderHistory(orderId);
    }
  };

  const handleStatus = async (orderId, status) => {
    try {
      await api.patch(`/api/staff/orders/${orderId}/status?restaurantId=${restaurantId}`, { status });
      await api.patch(`/api/staff/orders/${orderId}/read?restaurantId=${restaurantId}`);
      fetchOrders();
      fetchStats();
      if (expandedOrderId === orderId) fetchOrderHistory(orderId);
    } catch { showMsg('❌ Ошибка смены статуса'); }
  };

  const handleReadCall = async (callId) => {
    setMessage('');
    try {
      await api.patch(`/api/staff/calls/${callId}/read?restaurantId=${restaurantId}`);
      notifiedCallIdsRef.current.add(callId);
      await fetchCalls();
    } catch { showMsg('❌ Ошибка'); }
  };

  const filteredOrders = statusFilter === 'all'
    ? orders
    : orders.filter((o) => o.status === statusFilter);

  const groupedOrders = groupByDay(filteredOrders);
  const unreadOrders  = orders.filter((o) => !o.is_read).length;
  const unreadCalls   = calls.filter((c) => !c.is_read).length;

  const PeriodBar = () => (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
        {[
          { key: 'today',     label: 'Сегодня'  },
          { key: 'yesterday', label: 'Вчера'    },
          { key: 'week',      label: '7 дней'   },
          { key: 'month',     label: '30 дней'  },
          { key: 'all',       label: 'Всё время'},
          { key: 'custom',    label: '📅 Период' },
        ].map((p) => (
          <button key={p.key} onClick={() => setPeriod(p.key)} style={{
            padding: '6px 12px', borderRadius: '100px', border: 'none',
            backgroundColor: period === p.key ? '#000' : '#F0F0F0',
            color: period === p.key ? '#fff' : '#333',
            fontSize: '12px', fontWeight: '600', cursor: 'pointer',
            flexShrink: 0,
          }}>{p.label}</button>
        ))}
      </div>
      {period === 'custom' && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input type="date" value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)} style={inputStyle} />
          <span style={{ color: '#999', fontSize: '13px' }}>—</span>
          <input type="date" value={dateTo}
            onChange={(e) => setDateTo(e.target.value)} style={inputStyle} />
        </div>
      )}
    </div>
  );

  const StatsBlock = () => {
    if (!stats) return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
        Загрузка...
      </div>
    );
    const t = stats.total;
    return (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
          {[
            { label: 'Заказов',   value: t.orders_count,   color: '#007AFF', icon: '📋' },
            {
              label: 'Выручка',
              value: formatRestaurantPrice(t.revenue, menuCurrency),
              color: '#34C759',
              icon: '💰',
              small: true,
            },
            { label: 'Выдано',    value: t.completed_count, color: '#8E8E93', icon: '✅' },
            { label: 'Отменено',  value: t.cancelled_count, color: '#E85D4A', icon: '❌' },
          ].map((k, i) => (
            <div key={i} style={{
              backgroundColor: k.color, borderRadius: '16px',
              padding: '16px', color: '#fff',
            }}>
              <div style={{ fontSize: '24px', marginBottom: '4px' }}>{k.icon}</div>
              <div style={{ fontSize: k.small ? '16px' : '28px', fontWeight: '800', lineHeight: 1 }}>
                {k.value}
              </div>
              <div style={{ fontSize: '12px', opacity: 0.85, marginTop: '4px' }}>{k.label}</div>
            </div>
          ))}
        </div>

        {ordersMixedCurrency && (
          <div style={{
            fontSize: '12px', color: '#888', marginBottom: '10px', lineHeight: 1.35,
          }}>
            В периоде заказы в разных валютах: блок «Итоги» считает сумму числом в одной метке — смотри разбивку в списке заказов.
          </div>
        )}

        {Number(t.orders_count) > 0 && (
          <div style={{
            backgroundColor: '#fff', borderRadius: '14px', padding: '14px 16px',
            marginBottom: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span style={{ fontSize: '14px', color: '#555' }}>🧾 Средний чек</span>
            <span style={{ fontSize: '16px', fontWeight: '700' }}>
              {formatRestaurantPrice(
                Math.round(Number(t.revenue) / Number(t.orders_count)),
                menuCurrency
              )}
            </span>
          </div>
        )}

        {stats.topItems?.length > 0 && (
          <div style={{
            backgroundColor: '#fff', borderRadius: '14px', padding: '14px',
            marginBottom: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}>
            <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '12px' }}>
              🏆 Топ блюд
            </div>
            {stats.topItems.map((item, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 0',
                borderBottom: i < stats.topItems.length - 1 ? '1px solid #F5F5F5' : 'none',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                    width: '26px', height: '26px', borderRadius: '50%', flexShrink: 0,
                    backgroundColor: i === 0 ? '#FFD700' : i === 1 ? '#C0C0C0' : i === 2 ? '#CD7F32' : '#F0F0F0',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '12px', fontWeight: '700',
                  }}>{i + 1}</div>
                  <span style={{ fontSize: '13px' }}>{item.name}</span>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#333' }}>{item.total_qty} шт</div>
                  {item.total_sum != null && (
                    <div style={{ fontSize: '12px', color: '#34C759', fontWeight: '600' }}>
                      {formatRestaurantPrice(item.total_sum, menuCurrency)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {stats.byDay?.length > 0 && (
          <div style={{
            backgroundColor: '#fff', borderRadius: '14px', padding: '14px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}>
            <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '12px' }}>
              📅 По дням
            </div>
            {stats.byDay.map((d, i) => {
              const maxRev = Math.max(...stats.byDay.map((x) => Number(x.revenue)));
              const pct = maxRev > 0 ? (Number(d.revenue) / maxRev) * 100 : 0;
              return (
                <div key={i} style={{ marginBottom: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontSize: '13px', color: '#555' }}>
                      {new Date(d.day).toLocaleDateString('ru-RU', {
                        day: 'numeric', month: 'short', weekday: 'short',
                      })}
                    </span>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ fontSize: '13px', fontWeight: '700' }}>
                        {formatRestaurantPrice(d.revenue, menuCurrency)}
                      </span>
                      <span style={{ fontSize: '12px', color: '#999', marginLeft: '6px' }}>
                        {d.orders_count} зак.
                      </span>
                    </div>
                  </div>
                  <div style={{ height: '6px', backgroundColor: '#F0F0F0', borderRadius: '3px' }}>
                    <div style={{
                      height: '6px', borderRadius: '3px',
                      backgroundColor: '#007AFF',
                      width: `${pct}%`,
                      transition: 'width 0.4s ease',
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {Number(t.orders_count) === 0 && (
          <div style={{ textAlign: 'center', color: '#999', padding: '30px', fontSize: '14px' }}>
            За выбранный период заказов нет
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {message && (
        <div style={{
          padding: '12px 16px', borderRadius: '12px', marginBottom: '16px',
          backgroundColor: message.startsWith('❌') ? '#FFEBEE' : '#E8F5E9',
          fontWeight: '500', textAlign: 'center', fontSize: '14px',
        }}>{message}</div>
      )}

      <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
        {[
          { key: 'orders',   label: '📋 Заказы',  badge: unreadOrders },
          { key: 'calls',    label: '🔔 Вызовы',  badge: unreadCalls  },
          { key: 'banquets', label: '🥂 Банкеты', badge: 0 },
          ...(userRole !== 'waiter' ? [
            { key: 'stats',  label: '📊 Итоги',   badge: 0 },
          ] : []),
        ].map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            flex: 1, padding: '10px 4px', borderRadius: '12px', border: 'none',
            backgroundColor: tab === t.key ? '#000' : '#F0F0F0',
            color: tab === t.key ? '#fff' : '#333',
            fontSize: '12px', fontWeight: '600', cursor: 'pointer', position: 'relative',
          }}>
            {t.label}
            {t.badge > 0 && (
              <span style={{
                position: 'absolute', top: '-6px', right: '-4px',
                backgroundColor: '#E85D4A', color: '#fff', borderRadius: '50%',
                width: '18px', height: '18px', fontSize: '10px', fontWeight: '700',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>{t.badge}</span>
            )}
          </button>
        ))}
      </div>

      {tab === 'orders' && (
        <div>
          <PeriodBar />
          <div style={{
            display: 'flex', gap: '6px', overflowX: 'auto',
            paddingBottom: '8px', marginBottom: '12px', scrollbarWidth: 'none',
          }}>
            {[
              { key: 'all',       label: 'Все'       },
              { key: 'pending',   label: '🟠 Новые'  },
              { key: 'confirmed', label: '🔵 Приняты'},
              { key: 'ready',     label: '🟢 Готовы' },
              { key: 'completed', label: '✅ Выданы' },
              { key: 'cancelled', label: '❌ Отменены'},
            ].map((s) => (
              <button key={s.key} onClick={() => setStatusFilter(s.key)} style={{
                flexShrink: 0, padding: '5px 12px', borderRadius: '100px', border: 'none',
                backgroundColor: statusFilter === s.key ? '#000' : '#F0F0F0',
                color: statusFilter === s.key ? '#fff' : '#555',
                fontSize: '12px', fontWeight: '600', cursor: 'pointer',
              }}>{s.label}</button>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', marginBottom: '10px', gap: '8px', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '13px', color: '#8E8E93' }}>
              {filteredOrders.length} заказов
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setShowCreateOrder(true)} style={{
                ...refreshBtn,
                backgroundColor: '#34C759',
                color: '#fff',
              }}>
                + Создать заказ
              </button>
              <button onClick={() => { fetchOrders(); fetchStats(); }} style={refreshBtn}>
                🔄 Обновить
              </button>
            </div>
          </div>

          {showCreateOrder && (
            <CreateOrderModal
              menuItems={menuItems}
              categories={categories}
              menuCurrency={menuCurrency}
              onClose={() => setShowCreateOrder(false)}
              onSubmit={async (payload) => {
                setCreateOrderLoading(true);
                try {
                  await api.post('/api/staff/orders/create', {
                    ...payload,
                    restaurantId,
                  });
                  showMsg('✅ Заказ создан!');
                  setShowCreateOrder(false);
                  fetchOrders();
                  fetchStats();
                } catch (err) {
                  showMsg('❌ ' + (err.response?.data?.error || 'Ошибка'));
                } finally {
                  setCreateOrderLoading(false);
                }
              }}
              loading={createOrderLoading}
            />
          )}

          {editOrderId && (() => {
            const order = orders.find((o) => o.id === editOrderId);
            return order ? (
              <EditOrderModal
                key={editOrderId}
                order={order}
                menuItems={menuItems}
                categories={categories}
                menuCurrency={menuCurrency}
                onClose={() => setEditOrderId(null)}
                onSubmit={async (payload) => {
                  setEditOrderLoading(true);
                  try {
                    await api.patch(`/api/staff/orders/${payload.orderId}/items`, {
                      items: payload.items,
                      restaurantId,
                    });
                    showMsg('✅ Заказ обновлён!');
                    setEditOrderId(null);
                    fetchOrders();
                    fetchStats();
                  } catch (err) {
                    showMsg('❌ ' + (err.response?.data?.error || 'Ошибка'));
                  } finally {
                    setEditOrderLoading(false);
                  }
                }}
                loading={editOrderLoading}
              />
            ) : null;
          })()}

          {filteredOrders.length === 0 && (
            <div style={{ textAlign: 'center', color: '#999', padding: '40px', fontSize: '14px' }}>
              {period === 'today' ? 'Сегодня заказов нет' : 'Заказов за период нет'}
            </div>
          )}

          {Object.entries(groupedOrders).map(([day, dayOrders]) => (
            <div key={day}>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 4px 6px', marginBottom: '6px',
                borderBottom: '2px solid #F0F0F0',
              }}>
                <div style={{ fontWeight: '700', fontSize: '14px' }}>{day}</div>
                <div style={{ fontSize: '12px', color: '#8E8E93' }}>
                  {dayOrders.filter(o => o.status !== 'cancelled').length} зак. ·{' '}
                  <span style={{ fontWeight: '600', color: '#34C759' }}>
                    {formatMoneyMix(dayOrders)}
                  </span>
                </div>
              </div>

              {dayOrders.map((order) => {
                const st = STATUS_LABELS[order.status] || STATUS_LABELS.pending;
                const isExpanded = expandedOrderId === order.id;
                const history = orderHistory[order.id];
                return (
                  <div key={order.id} style={{
                    backgroundColor: '#fff', borderRadius: '16px', padding: '16px',
                    marginBottom: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    borderLeft: order.is_read ? '4px solid transparent' : '4px solid #FF9500',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'flex-start', marginBottom: '10px' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '16px', fontWeight: '700' }}>
                          Заказ #{order.order_number}
                        </div>
                        <div style={{ fontSize: '13px', color: '#8E8E93', marginTop: '2px' }}>
                          🪑 Стол: <strong>{order.table_number || '—'}</strong>
                          {' · '}{fmtT(order.created_at)}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                        <span style={{
                          padding: '4px 12px', borderRadius: '20px',
                          backgroundColor: st.bg, color: st.color,
                          fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap',
                        }}>{st.label}</span>
                        <button
                          onClick={() => toggleOrderExpand(order.id)}
                          style={{
                            width: '32px', height: '32px', borderRadius: '8px',
                            border: 'none', backgroundColor: isExpanded ? '#000' : '#F0F0F0',
                            color: isExpanded ? '#fff' : '#666',
                            fontSize: '14px', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transform: isExpanded ? 'rotate(180deg)' : 'none',
                          }}
                          aria-label={isExpanded ? 'Свернуть' : 'История'}
                        >▼</button>
                      </div>
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                      {(order.items || []).filter(Boolean).map((item, i) => (
                        <div key={i} style={{
                          display: 'flex', justifyContent: 'space-between',
                          fontSize: '13px', padding: '4px 0',
                          borderBottom: '1px solid #F5F5F5',
                        }}>
                          <span>{item.name} × {item.quantity}</span>
                          <span style={{ color: '#666' }}>
                            {formatRestaurantPrice(
                              Number(item.price) * item.quantity,
                              normalizeCurrency(order.currency)
                            )}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between',
                      fontWeight: '700', fontSize: '15px', marginBottom: '12px',
                      paddingTop: '8px', borderTop: '1px solid #F0F0F0' }}>
                      <span>Итого</span>
                      <span>
                        {formatRestaurantPrice(order.total_amount, normalizeCurrency(order.currency))}
                      </span>
                    </div>

                    {order.status !== 'completed' && order.status !== 'cancelled' && (
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {order.status === 'pending' && (
                          <button onClick={() => handleStatus(order.id, 'confirmed')}
                            style={btnStyle('#007AFF')}>✅ Принять</button>
                        )}
                        {order.status === 'confirmed' && (
                          <button onClick={() => handleStatus(order.id, 'ready')}
                            style={btnStyle('#34C759')}>🍽️ Готов</button>
                        )}
                        {order.status === 'ready' && (
                          <button onClick={() => handleStatus(order.id, 'completed')}
                            style={btnStyle('#8E8E93')}>✔️ Выдан</button>
                        )}
                        {['pending', 'confirmed'].includes(order.status) && (
                          <button onClick={() => setEditOrderId(order.id)}
                            style={btnStyle('#8E8E93')}>✏️ Изменить</button>
                        )}
                        <button onClick={() => handleStatus(order.id, 'cancelled')}
                          style={btnStyle('#E85D4A')}>✕ Отмена</button>
                      </div>
                    )}

                    {isExpanded && (
                      <div style={{
                        marginTop: '14px', paddingTop: '14px',
                        borderTop: '1px solid #F0F0F0',
                      }}>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '8px' }}>
                          📋 История статусов
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: '10px',
                            fontSize: '12px', color: '#8E8E93',
                          }}>
                            <span style={{
                              width: '8px', height: '8px', borderRadius: '50%',
                              backgroundColor: '#34C759', flexShrink: 0,
                            }} />
                            <span>Создан</span>
                            <span style={{ marginLeft: 'auto' }}>{fmtT(order.created_at)}</span>
                          </div>
                          {Array.isArray(history) ? history.map((h, i) => {
                            const hs = STATUS_LABELS[h.status] || STATUS_LABELS.pending;
                            return (
                              <div key={i} style={{
                                display: 'flex', alignItems: 'center', gap: '10px',
                                fontSize: '12px',
                              }}>
                                <span style={{
                                  width: '8px', height: '8px', borderRadius: '50%',
                                  backgroundColor: hs.color, flexShrink: 0,
                                }} />
                                <span style={{ fontWeight: '500' }}>{hs.label}</span>
                                {h.changed_by_email && (
                                  <span style={{ color: '#8E8E93', fontSize: '11px' }}>
                                    ({h.changed_by_email})
                                  </span>
                                )}
                                <span style={{ marginLeft: 'auto', color: '#8E8E93' }}>
                                  {fmtT(h.created_at)}
                                </span>
                              </div>
                            );
                          }) : (
                            <div style={{ fontSize: '12px', color: '#999' }}>Загрузка...</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {tab === 'calls' && (
        <div>
          <PeriodBar />
          <div style={{ display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', marginBottom: '10px' }}>
            <div style={{ fontSize: '13px', color: '#8E8E93' }}>
              {calls.length} вызовов · {calls.filter(c => !c.is_read).length} необработано
            </div>
            <button onClick={fetchCalls} style={refreshBtn}>🔄 Обновить</button>
          </div>

          {calls.length === 0 && (
            <div style={{ textAlign: 'center', color: '#999', padding: '40px', fontSize: '14px' }}>
              Вызовов нет
            </div>
          )}

          {calls.map((call) => {
            const lbl = CALL_LABELS[call.type] || CALL_LABELS.waiter;
            return (
              <div key={call.id} style={{
                backgroundColor: '#fff', borderRadius: '16px', padding: '16px',
                marginBottom: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                borderLeft: call.is_read ? '4px solid transparent' : `4px solid ${lbl.color}`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                opacity: call.is_read ? 0.6 : 1,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '44px', height: '44px', borderRadius: '12px',
                    backgroundColor: lbl.bg, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '22px',
                  }}>{lbl.icon}</div>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '14px' }}>{lbl.label}</div>
                    <div style={{ fontSize: '12px', color: '#8E8E93', marginTop: '2px' }}>
                      🪑 Стол {call.table_number || '?'} · {fmtT(call.created_at)}
                    </div>
                    <div style={{ fontSize: '11px', color: '#bbb', marginTop: '1px' }}>
                      {fmtD(call.created_at)}
                    </div>
                  </div>
                </div>
                {!call.is_read ? (
                  <button onClick={() => handleReadCall(call.id)} style={{
                    padding: '8px 14px', borderRadius: '10px', border: 'none',
                    backgroundColor: '#000', color: '#fff',
                    fontSize: '12px', fontWeight: '600', cursor: 'pointer', flexShrink: 0,
                  }}>Готово</button>
                ) : (
                  <span style={{ fontSize: '12px', color: '#8E8E93', flexShrink: 0 }}>✓</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab === 'banquets' && (
        <BanquetsTab api={api} restaurantId={restaurantId} userRole={userRole}
          menuItems={menuItems} categories={categories} />
      )}

      {tab === 'stats' && userRole !== 'waiter' && (
        <div>
          <PeriodBar />
          <StatsBlock />
        </div>
      )}
    </div>
  );
}

const btnStyle = (color) => ({
  padding: '8px 14px', borderRadius: '10px', border: 'none',
  backgroundColor: color, color: '#fff',
  fontSize: '13px', fontWeight: '600', cursor: 'pointer',
});

const refreshBtn = {
  padding: '6px 12px', borderRadius: '8px', border: 'none',
  backgroundColor: '#F0F0F0', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
};

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: '10px',
  border: '1px solid #E0E0E0', fontSize: '14px',
  boxSizing: 'border-box', outline: 'none',
};
